import React, { useState, useRef, useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc, arrayUnion } from 'firebase/firestore';

const STEPS = ['riddle', 'answer', 'name'];

function RiddleSubmitForm() {
  const [step, setStep] = useState(0);
  const [riddle, setRiddle] = useState('');
  const [answer, setAnswer] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [step]);

  const canAdvance = () => {
    if (step === 0) return riddle.trim().length > 0;
    if (step === 1) return answer.trim().length > 0;
    return true;
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep(s => s + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < STEPS.length - 1) { handleNext(); return; }

    setStatus('loading');
    const today = new Date().toISOString().split('T')[0];
    const entry = {
      riddle: riddle.trim(),
      answer: answer.toLowerCase().trim(),
      ...(name.trim() && { thanks: name.trim() }),
    };

    try {
      const ref = doc(db, 'riddles', today);
      await setDoc(ref, { riddles: arrayUnion(entry) }, { merge: true });
      setStatus('success');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && step !== 0) {
      e.preventDefault();
      if (canAdvance()) handleSubmit(e);
    }
  };

  if (status === 'success') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <img src="/assets-in-site/icon.png" alt="RiddleMe" style={s.logo} />
          <div style={s.successIcon}>🎉</div>
          <h2 style={s.successTitle}>Thanks!</h2>
          <p style={s.successBody}>Your riddle has been submitted. We'll review it and it may appear in the app soon.</p>
          <button style={s.btn} onClick={() => { setStatus(null); setStep(0); setRiddle(''); setAnswer(''); setName(''); }}>
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <img src="/assets-in-site/icon.png" alt="RiddleMe" style={s.logo} />
        <h1 style={s.appName}>RiddleMe</h1>

        <div style={s.dots}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ ...s.dot, background: i <= step ? '#7c6ff7' : 'rgba(255,255,255,0.2)' }} />
          ))}
        </div>

        <form onSubmit={handleSubmit} style={s.form}>
          {step === 0 && (
            <div style={s.stepBlock}>
              <label style={s.question}>What's your riddle?</label>
              <p style={s.hint}>We may shorten it slightly for the app.</p>
              <textarea
                ref={inputRef}
                value={riddle}
                onChange={e => setRiddle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && canAdvance()) { e.preventDefault(); handleNext(); } }}
                placeholder="What has hands but can't clap?"
                rows={4}
                style={s.textarea}
              />
            </div>
          )}

          {step === 1 && (
            <div style={s.stepBlock}>
              <label style={s.question}>What's the answer?</label>
              <p style={s.hint}>Keep it to one word.</p>
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={e => setAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Clock"
                style={s.input}
              />
            </div>
          )}

          {step === 2 && (
            <div style={s.stepBlock}>
              <label style={s.question}>Your name?</label>
              <p style={s.hint}>Optional. We'll credit you in the app.</p>
              <input
                ref={inputRef}
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. Jane Smith"
                style={s.input}
              />
            </div>
          )}

          <div style={s.actions}>
            {step > 0 && (
              <button type="button" style={s.backBtn} onClick={() => setStep(s => s - 1)}>
                Back
              </button>
            )}
            <button
              type="submit"
              style={{ ...s.btn, opacity: (!canAdvance() || status === 'loading') ? 0.45 : 1, flex: 1 }}
              disabled={!canAdvance() || status === 'loading'}
            >
              {status === 'loading' ? 'Submitting…' : step < STEPS.length - 1 ? 'Next' : 'Submit'}
            </button>
          </div>

          {step === 2 && (
            <button type="button" style={s.skipBtn} onClick={handleSubmit}>
              Skip and submit
            </button>
          )}

          {status === 'error' && (
            <p style={s.errorMsg}>Something went wrong. Please try again.</p>
          )}
        </form>
      </div>
    </div>
  );
}

const s = {
  page: {
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    color: '#fff',
  },
  card: {
    maxWidth: 420,
    width: '100%',
    textAlign: 'center',
  },
  logo: {
    width: 64,
    height: 64,
    borderRadius: 14,
    marginBottom: 12,
  },
  appName: {
    fontSize: 24,
    fontWeight: 700,
    margin: '0 0 16px',
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 24,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    transition: 'background 0.3s',
  },
  form: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 16,
    padding: '24px 20px 20px',
    textAlign: 'left',
  },
  stepBlock: {
    marginBottom: 20,
  },
  question: {
    display: 'block',
    fontSize: 17,
    fontWeight: 600,
    color: '#f0f0f0',
    marginBottom: 4,
  },
  hint: {
    fontSize: 13,
    color: '#a0aec0',
    margin: '0 0 12px',
  },
  input: {
    width: '100%',
    padding: '13px 14px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 10,
    color: '#fff',
    fontSize: 16,
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '13px 14px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 10,
    color: '#fff',
    fontSize: 16,
    boxSizing: 'border-box',
    resize: 'vertical',
    outline: 'none',
    fontFamily: 'inherit',
  },
  actions: {
    display: 'flex',
    gap: 10,
  },
  btn: {
    padding: '13px 20px',
    background: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  backBtn: {
    padding: '13px 18px',
    background: 'rgba(255,255,255,0.1)',
    color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
  },
  skipBtn: {
    display: 'block',
    width: '100%',
    marginTop: 12,
    background: 'none',
    border: 'none',
    color: '#a0aec0',
    fontSize: 13,
    cursor: 'pointer',
    textAlign: 'center',
    padding: 0,
  },
  successIcon: {
    fontSize: 48,
    margin: '8px 0 12px',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 700,
    margin: '0 0 8px',
  },
  successBody: {
    fontSize: 15,
    color: '#a0aec0',
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  errorMsg: {
    marginTop: 12,
    fontSize: 13,
    color: '#fc8181',
    textAlign: 'center',
  },
};

export default RiddleSubmitForm;
